import { cn } from '@/lib/utils';

/** Heuristic: does this text look like a unified diff? */
export function looksLikeDiff(text: string): boolean {
  const lines = text.split('\n');
  const marked = lines.filter((l) => /^[+-](?![+-]{2})/.test(l) || l.startsWith('@@')).length;
  return marked >= 2 && marked >= lines.length * 0.4;
}

/** Full-width red/green tinted diff rows with line numbers (old/new gutters). */
export function DiffRows({ text, bleed = true }: { text: string; bleed?: boolean }) {
  let oldNo = 1;
  let newNo = 1;
  const rows = text.split('\n').map((line) => {
    const hunk = /^@@ -(\d+)(?:,\d+)? \+(\d+)/.exec(line);
    if (hunk) {
      oldNo = Number(hunk[1]);
      newNo = Number(hunk[2]);
      return { kind: 'hunk' as const, line, no: '' };
    }
    if (line.startsWith('+')) return { kind: 'add' as const, line: line.slice(1), no: String(newNo++) };
    if (line.startsWith('-')) return { kind: 'del' as const, line: line.slice(1), no: String(oldNo++) };
    oldNo++;
    return { kind: 'ctx' as const, line: line.startsWith(' ') ? line.slice(1) : line, no: String(newNo++) };
  });
  return (
    <div className={cn('my-0.5', bleed && '-mx-3')}>
      {rows.map((r, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: diff rows are positional
          key={i}
          className={cn(
            'grid grid-cols-[3.5rem_1.25rem_minmax(0,1fr)] px-3 whitespace-pre-wrap',
            r.kind === 'add' && 'diff-add',
            r.kind === 'del' && 'diff-del',
            r.kind === 'hunk' && 'text-faint',
          )}
        >
          <span className="pr-2 text-right text-faint select-none">{r.no}</span>
          <span
            className={cn(
              'select-none',
              r.kind === 'add' ? 'text-success' : r.kind === 'del' ? 'text-danger' : 'text-faint',
            )}
          >
            {r.kind === 'add' ? '+' : r.kind === 'del' ? '−' : ' '}
          </span>
          <span className="min-w-0 break-words max-lg:break-all">{r.line || ' '}</span>
        </div>
      ))}
    </div>
  );
}
